import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import UserCompensations from './UserCompensations';
import UserAccountService from '../../services/userAccountService';
import { AppPage, AppCard } from '../../components/general';
import {
  FormContainer,
  FormDropdown,
  FormInputText,
  FormMultiSelect,
  FormRadioGroup,
  FormPassword,
} from '../../components/form';
import { UserAccountDto } from '../../models/user-account';
import OrgUnitsService from '../../services/orgUnitsService';
import { OrganizationalUnit } from '../../models/org-units/orgUnit';
import { TimezoneOptions } from '../../utils/constants';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';

const EditUserAccount = (): JSX.Element => {
  const errors = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [orgUnits, setOrgUnits] = useState<OrganizationalUnit[]>([]);
  const [federatedAuthProviders, setFederatedAuthProviders] = useState<any[]>([]);
  const [authMethod, setAuthMethod] = useState<'password' | 'federated'>('password');

  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useAuth();

  const userAccountService = UserAccountService.getInstance();
  const orgUnitsService = OrgUnitsService.getInstance();

  const availableRoles = useMemo(() => {
    const allRoles = [
      { label: 'User', value: 'User' },
      { label: 'Admin', value: 'Admin' },
      { label: 'SuperAdmin', value: 'SuperAdmin' },
    ];

    const userRoles: string[] = user?.roles || [];
    const isSuperAdmin = userRoles.includes('SuperAdmin');

    if (isSuperAdmin) {
      return allRoles;
    }

    return allRoles.filter((role) => role.value !== 'SuperAdmin');
  }, [user]);

  useEffect(() => {
    const apiCalls: (Promise<OrganizationalUnit[]> | Promise<UserAccountDto> | Promise<any[]>)[] = [
      orgUnitsService.getAll(),
      userAccountService.getAvailableFederatedAuthProviders(),
    ];
    if (userId) {
      apiCalls.push(userAccountService.get(userId));
    }

    setLoading(true);
    Promise.all(apiCalls)
      .then((results) => {
        const sortedOrgUnits = [...(results[0] as OrganizationalUnit[])].sort((a, b) => a.name.localeCompare(b.name));
        setOrgUnits(sortedOrgUnits);
        setFederatedAuthProviders(results[1] as any[]);

        if (results[2]) {
          const userData = results[2] as UserAccountDto;
          reset(userData);
          setAuthMethod(userData.federatedAuthProviderId ? 'federated' : 'password');
        }
      })
      .catch((error) => showError(error.message))
      .finally(() => setLoading(false));
  }, []);

  const validationSchema = yup.object({
    email: yup.string().nullable().required('Email is required').trim(),
    firstName: yup.string().nullable().required('First name is required').trim(),
    lastName: yup.string().nullable().required('Last name is required').trim(),
    roles: yup.array().nullable().required('A role is required'),
    primaryOrgUnitId: yup.string().nullable(),
    nmlsId: yup.string().nullable(),
    timezone: yup.string().nullable().required('Timezone is required'),
    authMethod: yup.string().required('Authentication method is required'),
    federatedAuthProviderId: yup.string().when('authMethod', {
      is: 'federated',
      then: (schema) => schema.required('Federated auth provider is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    password: yup.string().when('authMethod', {
      is: (authMethod: string) => authMethod === 'password' && !userId,
      then: (schema) => schema.required('Password is required'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
    reset,
  } = useForm<UserAccountDto>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: {
      id: '',
      email: '',
      tenantId: '',
      firstName: '',
      timezone: '',
      address: {
        street: '',
        city: '',
        county: '',
        unitNumber: '',
        state: '',
        postalCode: '',
        latitude: 0,
        longitude: 0,
      },
      nmlsId: '',
      primaryOrgUnitId: '',
      lastName: '',
      updatedDate: '',
      updatedBy: '',
      roles: [],
      authMethod: 'password',
      federatedAuthProviderId: '',
      password: '',
    },
  });

  const watchedAuthMethod = useWatch({
    control,
    name: 'authMethod',
    defaultValue: 'password',
  });

  useEffect(() => {
    if (watchedAuthMethod) {
      setAuthMethod(watchedAuthMethod as 'password' | 'federated');
    }
  }, [watchedAuthMethod]);

  const showError = (error: string) => {
    errors.current.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const onSubmit = handleSubmit((account: UserAccountDto) => {
    const submitAccount = { ...account };

    if (submitAccount.authMethod === 'federated') {
      delete submitAccount.password;
    } else {
      delete submitAccount.federatedAuthProviderId;
    }

    if (!submitAccount.primaryOrgUnitId || submitAccount.primaryOrgUnitId.trim() === '') {
      submitAccount.primaryOrgUnitId = null;
    }

    // Remove tenantId when creating - backend auto-populates from AppContext
    if (!userId) {
      delete submitAccount.tenantId;
    }

    confirmDialog({
      message: `Are you sure you want to save this account: ${account.email}?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const addOrUpdate = !userId ? userAccountService.create(submitAccount) : userAccountService.update(submitAccount);

        setLoading(true);
        addOrUpdate
          .then(() => {
            navigateToUserAccounts();
          })
          .catch((error) => {
            showError(error.message);
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  });

  const navigateToUserAccounts = () => {
    navigate('/user-accounts');
  };

  return (
    <AppPage title="User Account Details" breadcrumbs="Admin - User Account Details">
      <ConfirmDialog />
      <AppCard>
        <TabView>
          <TabPanel header="Account Details">
            <FormContainer loading={loading} errors={errors} onSubmit={onSubmit} onCancel={navigateToUserAccounts}>
              <FormInputText
                control={control}
                errors={formErrors}
                field="id"
                label="ID"
                disabled={true}
                className="hidden"
              />
              {/* Row 1: First Name, Last Name, Email */}
              <div className="!grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <FormInputText control={control} errors={formErrors} field="firstName" label="First Name" />
                <FormInputText control={control} errors={formErrors} field="lastName" label="Last Name" />
                <FormInputText control={control} errors={formErrors} field="email" label="Email" />
              </div>

              {/* Row 2: Primary Org, Roles */}
              <div className="!grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Controller
                  name="primaryOrgUnitId"
                  control={control}
                  render={({ field }: { field: any }) => (
                    <FormDropdown
                      control={control}
                      errors={formErrors}
                      field="primaryOrgUnitId"
                      label="Primary Org."
                      options={orgUnits}
                      optionLabel="name"
                      optionValue="orgUnitId"
                      showClear={true}
                      onChange={(e) => {
                        field.onChange(e.value);
                      }}
                    />
                  )}
                />
                <FormMultiSelect
                  control={control}
                  errors={formErrors}
                  field="roles"
                  label="Roles"
                  options={availableRoles}
                />
              </div>

              {/* Row 3: Auth Method Selection with inline inputs */}
              <div className="!grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FormRadioGroup
                  control={control}
                  errors={formErrors}
                  field="authMethod"
                  label="Authentication Method"
                  options={[
                    { label: 'Password', value: 'password' },
                    { label: 'Federated Authentication', value: 'federated' },
                  ]}
                />

                {/* Conditional Password Field */}
                {authMethod === 'password' && (
                  <div className="flex flex-col gap-2">
                    {userId && (
                      <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <i className="pi pi-lock text-blue-600"></i>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-blue-900">Password is set</div>
                          <div className="text-xs text-blue-700">
                            For security, the current password cannot be displayed. Enter a new password below to change it.
                          </div>
                        </div>
                      </div>
                    )}
                    <FormPassword
                      control={control}
                      errors={formErrors}
                      field="password"
                      label="Password"
                      toggleMask
                      feedback={false}
                      placeholder={userId ? 'Enter new password to change' : 'Enter password'}
                    />
                  </div>
                )}

                {/* Conditional Federated Auth Provider Selection */}
                {authMethod === 'federated' && (
                  <FormDropdown
                    control={control}
                    errors={formErrors}
                    field="federatedAuthProviderId"
                    label="Authentication Provider"
                    options={federatedAuthProviders}
                    optionLabel="name"
                    optionValue="federatedAuthProviderId"
                    placeholder="Select authentication provider"
                  />
                )}
              </div>

              {/* Row 4: NMLS# and Timezone */}
              <div className="!grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormInputText control={control} errors={formErrors} field="nmlsId" label="NMLS#" />
                <FormDropdown
                  control={control}
                  errors={formErrors}
                  field="timezone"
                  label="Timezone"
                  options={TimezoneOptions}
                  placeholder="Select timezone"
                  showClear={true}
                />
              </div>
            </FormContainer>
          </TabPanel>
          <TabPanel header="Compensation">
            <UserCompensations userId={userId} asPage={false} />
          </TabPanel>
        </TabView>
      </AppCard>
    </AppPage>
  );
};

export default EditUserAccount;
